/**
 * SetupPage — Formulario de configuración previo al escaneo.
 * Ruta: /
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { AppHeader, Card } from '../../shared/components';
import { getParam }        from '../../shared/theme';
import { ROUTES }          from '../../config/routes';
import { env }             from '../../config/env';
import {
  DEFAULT_FORM_VALUES,
  ADVANCED_OPTIONS,
  ID_OPTIONS,
  SAMPLE_TYPE_OPTIONS,
  EXPECTED_RESPONSE_OPTIONS,
} from './constants';
import type { CaptureFormValues, SetupLocationState } from './types';


// ─── Estilos locales ──────────────────────────────────────────────────────────
const inputCls =
  'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 ' +
  'shadow-sm transition focus:border-[--color-accent] focus:outline-none ' +
  'focus:ring-2 focus:ring-[--color-accent]/20 placeholder:text-gray-300';
const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400';

// ─── Componente ───────────────────────────────────────────────────────────────
export const SetupPage: React.FC = () => {
  useEffect(() => { document.body.classList.remove('modal-open'); }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const { dataOtorgante } = (location.state as SetupLocationState) ?? {};
  const logoSrc = getParam(dataOtorgante ?? [], 'LOGO_OTORGANTE');

  const [apikey,           setApikey]           = useState(env.blinkidApikey ?? '');
  const [idType,           setIdType]           = useState('INE');
  const [sampleType,       setSampleType]       = useState('REAL');
  const [expectedResponse, setExpectedResponse] = useState('Pass');
  const [formValues,       setFormValues]       = useState<CaptureFormValues>(DEFAULT_FORM_VALUES);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!apikey.trim()) {
      alert('Por favor ingresa el API Key antes de continuar.');
      return;
    }
    sessionStorage.setItem('idType',           idType);
    sessionStorage.setItem('sampleType',       sampleType);
    sessionStorage.setItem('expectedResponse', expectedResponse);
    document.body.classList.add('modal-open');
    navigate(ROUTES.capture, {
      state: {
        apikey:       apikey.trim(),
        dataOtorgante,
        formValues,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader logoSrc={logoSrc || undefined} />

      <main className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Formulario de prueba</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configura los parámetros antes de iniciar el escaneo.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          <Card title="Configuración">
            <div className="space-y-5">

              {/* API Key */}
              <div>
                <label className={labelCls}>API Key</label>
                <input
                  type="text"
                  value={apikey}
                  onChange={(e) => setApikey(e.target.value)}
                  placeholder="API Key del otorgante"
                  className={inputCls}
                />
              </div>

              {/* Selectores de prueba */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Tipo de identificación</label>
                  <select className={inputCls} value={idType} onChange={(e) => setIdType(e.target.value)}>
                    <option value="">Selecciona</option>
                    {ID_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Tipo de muestra</label>
                  <select className={inputCls} value={sampleType} onChange={(e) => setSampleType(e.target.value)}>
                    <option value="">Selecciona</option>
                    {SAMPLE_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Respuesta esperada</label>
                  <select className={inputCls} value={expectedResponse} onChange={(e) => setExpectedResponse(e.target.value)}>
                    <option value="">Selecciona</option>
                    {EXPECTED_RESPONSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

            </div>
          </Card>

          {env.enableConfig && (
            <Card title="Opciones avanzadas">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {(Object.keys(DEFAULT_FORM_VALUES) as Array<keyof CaptureFormValues>).map((key) => (
                  <div key={key}>
                    <label className={labelCls}>{key}</label>
                    <select
                      className={inputCls}
                      value={String(formValues[key])}
                      onChange={(e) => setFormValues({ ...formValues, [key]: e.target.value })}
                    >
                      <option value="">Selecciona</option>
                      {ADVANCED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <button type="submit" className="btn-primary w-full">
            Iniciar captura →
          </button>

        </form>
      </main>
    </div>
  );
};
